import { Component, OnInit, inject } from '@angular/core';
import { AppConfig } from 'app/app.config';
import { APIService } from 'app/services/api.service';
import { AuthenticationService } from 'app/services/auth.service';
import { LoaderService } from 'app/services/loader.service';
import {
  netWorthAPIDataType,
  categoryType,
  instrumentType,
  totalType,
  currencyCodeType,
  sortedClientInfoType,
  netWorthAPICustomClientAndValueAttributionType,
  netWorthAPIDataTypeCurrentNicolaWealthHeldAssetAttributionType,
  netWorthAPICustomInstrumentType,
  clientColumnsType
} from './net-worth.component.types';

import { MenuComponent } from 'app/components/Controls/menu/menu.component';
import { KeyValuePipe } from '@angular/common';
import {
  setHoldings,
  orderClients,
  setProxyForInstrumentHoldings,
  setCategories,
  buildClientNameToOrderMap,
  buildTargetNameIdToClientNameMap,
  sortCategories,
  setClientColumns,
  setTotals
} from "app/modules/net-worth/net-worth.fn";

@Component({
    templateUrl: './net-worth.component.html',
    styleUrls: ['./net-worth.component.css'],
    imports: [MenuComponent, KeyValuePipe]
})
export class NetWorthComponent implements OnInit {
  private api = inject(APIService);
  private auth = inject(AuthenticationService);
  private config = inject(AppConfig);
  private loaderService = inject(LoaderService);

  private rawCustomClientAndValues: netWorthAPICustomClientAndValueAttributionType[] = [];
  private rawHeldAssets: netWorthAPIDataTypeCurrentNicolaWealthHeldAssetAttributionType[] = [];
  private rawCustomInstruments: netWorthAPICustomInstrumentType[] = [];

  holdingsByProxyId: Record<string, instrumentType> = {};
  categoriesById: Record<string, categoryType> = {};
  categorySort: Record<string, number[]> = {};
  clientsOrdered: Record<string, sortedClientInfoType> = {};
  targetNameIdToClientNameMap: Record<string, string> = {};
  clientOverrideNameToOrderMap: Record<string, number> = {};
  clientColumns: clientColumnsType[] = [];

  asOfDate: string = '';
  fxRate: number = 0;
  loadError = false;
  totals: {
    totalsByCategory: Record<string, totalType>,
    totalsByParent: Record<string, totalType | undefined>,
    netWorthTotals: totalType,
  } = {
    totalsByCategory: {},
    totalsByParent: {},
    netWorthTotals: {
      total: 0,
      totalByClientOrder: {}
    },
  };
  viewToggle: Record<string, boolean> = {};

  ngOnInit(): void {
    this.loadNetWorthData();
  }

  private loadNetWorthData = () => {
    const reRg = this.auth.getCurrentReRg()!;
    this.loaderService.display(true, 'netWorth');
    this.api.get<netWorthAPIDataType>(`${this.config.apiUrl}NetWorth/NetWorthTableData?targetNameId=${reRg.ReRgId}`)
      .subscribe({
        next: (data: netWorthAPIDataType) => {
          this.fxRate = data.LatestUsdExchangeRate.USDToCADRate || 1;
          this.asOfDate = data.StatementDate.StatementDate || '';
          this.categorySort = sortCategories(data.Category || []);

          this.rawCustomClientAndValues = data.CustomClientAndValueAttribution || [];
          this.rawHeldAssets = data.CurrentNicolaWealthHeldAssetAttribution || [];
          this.rawCustomInstruments = data.CustomInstrument || [];

          const {proxyByHoldingId, proxyByInstrumentId} = setProxyForInstrumentHoldings(
            this.rawCustomClientAndValues,
            this.rawHeldAssets,
            this.rawCustomInstruments,
          );

          this.clientsOrdered = orderClients(
            this.rawHeldAssets,
            this.rawCustomClientAndValues
          );

          this.clientOverrideNameToOrderMap = buildClientNameToOrderMap(this.clientsOrdered);
          this.targetNameIdToClientNameMap = buildTargetNameIdToClientNameMap(this.clientsOrdered);

          this.holdingsByProxyId = setHoldings(
            this.clientsOrdered,
            this.clientOverrideNameToOrderMap,
            this.targetNameIdToClientNameMap,
            this.rawHeldAssets,
            this.rawCustomClientAndValues,
            this.rawCustomInstruments,
            proxyByInstrumentId,
            proxyByHoldingId,
            this.fxRate,
          );

          this.clientColumns = setClientColumns(this.clientsOrdered, this.holdingsByProxyId);

          this.categoriesById = setCategories(
            data.Category || [],
            this.holdingsByProxyId
          );

          this.totals = setTotals(
            this.holdingsByProxyId,
            this.categorySort,
            this.fxRate
          );

          this.loaderService.display(false, 'netWorth');
        },
        error: (error: unknown) => {
          this.loaderService.display(false, 'netWorth');
          this.loadError = true;
          console.log('error: ', error);
        },
      });
  };

  public currencyFormat = (value: number, fx?: number, currency?: currencyCodeType) => {
    if (fx && currency && currency !== 'CAD') {
      value *= fx;
    }
    return new Intl.NumberFormat("en-CA", { style: "currency", currency: "CAD" }).format(value);
  };

  public valueFormat = (value: number, fx?: number, currency?: currencyCodeType) => {
    if (fx && currency && currency !== 'CAD') {
      value *= fx;
    }
    return value.toLocaleString("en-CA", { maximumFractionDigits: 2, minimumFractionDigits: 2 });
  };

  public dateFormat = (date: string) => {
    const dateObject = new Date(date);
    return dateObject.toLocaleDateString("en-CA", { year: "numeric", month: "long", day: "numeric" });
  };

  public toggleView = (id: string) => {
    // will keep the display name column width the same on toggle close
    const displayNameColumn = document.getElementById('display-name-column');
    if (displayNameColumn) { displayNameColumn.style.width = `${displayNameColumn.offsetWidth}px`; }

    this.viewToggle[id] = !this.viewToggle[id];
  };
}
